import { useState, useEffect, useContext } from "react";
import Axios from "axios";
import { Data } from "../../Data";
import { useNotificationsContext } from "./NotificationsContext";
import { useNavCountsContext } from "./NavCountsContext";

function notifCountForPage(notifications, page) {
    return notifications.filter((n) => {
        const pages = Array.isArray(n.showOnPages) ? n.showOnPages : [];
        return pages.length === 0 || pages.includes(page);
    }).length;
}

export function useNavCounts() {
    const { state } = useContext(Data);
    const { userInfo } = state;
    const { notifications } = useNotificationsContext();
    const { refetchKey } = useNavCountsContext();

    const [counts, setCounts] = useState({
        casesCount: 0,
        documentsCount: 0,
        requestsCount: 0,
        profileCount: 0,
        userName: "",
        userPhone: "",
        userEmail: "",
        userAddress: "",
        loading: true,
    });

    useEffect(() => {
        if (!userInfo?.token) return;

        let cancelled = false;

        const fetchAll = async () => {
            try {
                const [infoRes, docsRes, reqRes] = await Promise.allSettled([
                    Axios.post("/api/info/", { request: "info" }, {
                        headers: { authorization: `Bearer ${userInfo.token}` },
                    }),
                    Axios.post("/api/documents/info", { request: "info" }, {
                        headers: { authorization: `Bearer ${userInfo.token}` },
                    }),
                    Axios.post("/api/request/", { request: "info" }, {
                        headers: { authorization: `Bearer ${userInfo.token}` },
                    }),
                ]);

                if (cancelled) return;

                const infoData = infoRes.status === "fulfilled" ? infoRes.value.data : {};
                const docsData = docsRes.status === "fulfilled" ? docsRes.value.data : {};
                const reqData  = reqRes.status  === "fulfilled" ? reqRes.value.data  : {};

                const baseCasesCount = Array.isArray(infoData.cases) ? infoData.cases.length : 0;

                const baseDocumentsCount = Array.isArray(docsData.cases)
                    ? docsData.cases.reduce((acc, c) => {
                          const unsigned = Array.isArray(c.signId)
                              ? c.signId.filter((s) => !s.isSigned).length
                              : 0;
                          return acc + unsigned;
                      }, 0)
                    : 0;

                const EXCLUDED_REQUEST_STATUSES = [
                    "Ukončeno",
                    "Ukončeno bez řešení",
                    "Nezpracováno",
                    "V řešení",
                ];
                const baseRequestsCount = Array.isArray(reqData.requests)
                    ? reqData.requests.filter((r) => {
                          const stateObj = Array.isArray(reqData.states)
                              ? reqData.states.find((s) => parseInt(s.id) === parseInt(r.stav))
                              : null;
                          const stateName = stateObj?.nazev_stavu ?? "";
                          return !EXCLUDED_REQUEST_STATUSES.includes(stateName);
                      }).length
                    : 0;

                setCounts((prev) => ({
                    ...prev,
                    _baseCases:     baseCasesCount,
                    _baseDocs:      baseDocumentsCount,
                    _baseRequests:  baseRequestsCount,
                    userName:    infoData.name         || "",
                    userPhone:   infoData.phone        || "",
                    userEmail:   infoData.email        || "",
                    userAddress: infoData.main_address || "",
                    loading: false,
                }));
            } catch {
                if (!cancelled) setCounts((prev) => ({ ...prev, loading: false }));
            }
        };

        fetchAll();
        return () => { cancelled = true; };
    }, [userInfo, refetchKey]);

    const casesCount     = (counts._baseCases    || 0) + notifCountForPage(notifications, "home");
    const documentsCount = (counts._baseDocs     || 0) + notifCountForPage(notifications, "documents");
    const requestsCount  = (counts._baseRequests || 0) + notifCountForPage(notifications, "requests");
    const profileCount   =                               notifCountForPage(notifications, "profile");

    return {
        casesCount,
        documentsCount,
        requestsCount,
        profileCount,
        userName:    counts.userName,
        userPhone:   counts.userPhone,
        userEmail:   counts.userEmail,
        userAddress: counts.userAddress,
        loading:     counts.loading,
    };
}
